import { useEffect, useState } from 'react'
import { createTrackedLink, deleteTrackedLink, getTrackedLinks } from '../../../../services/tracked-links'
import { getErrorMessage } from '../../../../utils/general'
import { errors } from '../../../../constants'

const useTrackedLinks = (siteDomain, email) => {
  const [linkName, setLinkName] = useState('')
  const [trackedLinks, setTrackedLinks] = useState([])
  const [isLoadingLinks, setIsLoadingLinks] = useState(false)
  const [isCreatingLink, setIsCreatingLink] = useState(false)
  const [isDeletingLink, setIsDeletingLink] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const fetchTrackedLinks = async () => {
    try {
      setIsLoadingLinks(true)
      setErrorMessage(null)
      const data = await getTrackedLinks(siteDomain, email)
      setTrackedLinks(data)
    } catch (err) {
      setErrorMessage(getErrorMessage(err) || 'Failed to fetch tracked links')
    } finally {
      setIsLoadingLinks(false)
    }
  }

  useEffect(() => {
    fetchTrackedLinks()
  }, [])

  const isLabelDuplicate = (label) => {
    return trackedLinks.some((link) => link.label.toLowerCase() === label.toLowerCase())
  }

  const handleCreateLink = async (e) => {
    e.preventDefault()
    const trimmedLabel = linkName.trim()

    if (!trimmedLabel) {
      setErrorMessage(getErrorMessage({ code: errors.TRACKED_LINKS_INVALID_INPUT }))
      return
    }

    if (trackedLinks.length >= 20) {
      setErrorMessage(getErrorMessage({ code: errors.TRACKED_LINKS_LIMIT_REACHED }))
      return
    }

    if (isLabelDuplicate(trimmedLabel)) {
      setErrorMessage(getErrorMessage({ code: errors.TRACKED_LINKS_LABEL_EXISTS }))
      return
    }

    try {
      setIsCreatingLink(true)
      setErrorMessage(null)

      const newLink = await createTrackedLink(siteDomain, trimmedLabel)

      setTrackedLinks((prev) => [...prev, { ...newLink, _entering: true }])
      setLinkName('')

      setTimeout(() => {
        setTrackedLinks((prev) =>
          prev.map((link) => (link.label === trimmedLabel ? { ...link, _entering: false } : link))
        )
      }, 300)
    } catch (err) {
      console.error(err)
      setErrorMessage(getErrorMessage(err) || 'Failed to create tracked link')
    } finally {
      setIsCreatingLink(false)
    }
  }

  const handleDeleteLink = async (linkLabel) => {
    try {
      setIsDeletingLink(true)
      setErrorMessage(null)

      setTrackedLinks((prev) => prev.map((link) => (link.label === linkLabel ? { ...link, _exiting: true } : link)))

      await new Promise((resolve) => setTimeout(resolve, 300))

      await deleteTrackedLink(siteDomain, linkLabel, email)

      setTrackedLinks((prev) => prev.filter((link) => link.label !== linkLabel))
    } catch (err) {
      console.error(err)
      setErrorMessage(getErrorMessage(err) || 'Failed to delete tracked link')
      setTrackedLinks((prev) => prev.map((link) => (link.label === linkLabel ? { ...link, _exiting: false } : link)))
    } finally {
      setIsDeletingLink(false)
    }
  }

  const handleRetry = () => {
    setErrorMessage(null)
    fetchTrackedLinks()
  }

  return {
    linkName,
    setLinkName,
    trackedLinks,
    isLoadingLinks,
    isCreatingLink,
    isDeletingLink,
    errorMessage,
    handleCreateLink,
    handleDeleteLink,
    handleRetry
  }
}

export default useTrackedLinks
